import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { RafflesFacade } from '@galabid/gb-dashboard/raffle/data-access'
import { SharedUiModule } from '@galabid/shared/ui'
import { TranslateModule } from '@ngx-translate/core'
import { RaffleDrawWidget } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  Subscription,
} from 'rxjs'
import { GbPresenterWidgetRaffleItemLabelComponent } from '../gb-presenter-widget-raffle-item-label/gb-presenter-widget-raffle-item-label.component'
import { PresenterConstants } from '../presenter.constants'
interface DigitColumn {
  digits: string[]
  isSpun: boolean
  style: Record<string, string>
}

interface DrawRowState {
  digitColumns: DigitColumn[]
  isAnimating: boolean
  spinningIndex: number
  spinIntervalId: ReturnType<typeof setInterval> | null
  spinTimeoutId: ReturnType<typeof setTimeout> | null
}

interface DrawQueueItem {
  ticketNumber: number
  prize: number
}

@Component({
  selector: 'gb-presenter-widget-raffle-draw',
  standalone: true,
  imports: [CommonModule, TranslateModule, GbPresenterWidgetRaffleItemLabelComponent, SharedUiModule],
  templateUrl: './gb-presenter-widget-raffle-draw.component.html',
  styleUrl: './gb-presenter-widget-raffle-draw.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetRaffleDrawComponent implements OnInit, OnChanges {
  private router = inject(Router)
  private cdr = inject(ChangeDetectorRef)
  private rafflesFacade = inject(RafflesFacade)
  private destroyRef = inject(DestroyRef)

  @Input() inPreviewMode = false
  @Input({ required: true }) raffleDrawWidget!: RaffleDrawWidget
  @Input() isTemplateMode = false
  @Output() confettiRequested = new EventEmitter<void>()
  @Output() openRaffleItemWarning = new EventEmitter<void>()

  get loaded$() {
    if (this.isTemplateMode) return of(true)
    return this.rafflesFacade.loaded$
  }

  drawRows: DrawRowState[] = []

  @Input() set testData(value: boolean) {
    this.showTestData = value
    if (!value) {
      this.resetPreview()
      this.cdr.markForCheck()
      return
    }

    this.setTestData()
  }

  showTestData = false

  private readonly SPIN_DELAY = 140
  private readonly SPIN_ANIMATION_TIME = 2600
  private readonly MIN_COLUMNS = 6
  private readonly MIN_FULL_SPINS = 3
  private readonly LANDING_LEAD_TIME_MS = 220

  private lastTicketNumber = 0
  private prizesAvailable = 1
  private raffleItemSubscription: Subscription | null = null

  private drawQueue: DrawQueueItem[] = []
  private queueTimeoutId: ReturnType<typeof setTimeout> | null = null
  private isQueueProcessing = false

  isLaunchMode$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.router.url.includes(`/${PresenterConstants.VIEW}`)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  isLaunchMode = toSignal(this.isLaunchMode$, { initialValue: false })

  raffleDrawWidget$ = new ReplaySubject<RaffleDrawWidget>(1)

  raffleItemName$: Observable<string | undefined> = combineLatest([
    this.rafflesFacade.allRaffleItems$,
    this.raffleDrawWidget$,
  ]).pipe(
    map(([items, widget]) => items.find((item) => item.raffle_item?.uuid === widget?.raffle_item)?.name),
    distinctUntilChanged(),
    shareReplay(1),
  )

  raffleItemName = toSignal(this.raffleItemName$)

  get totalAnimationTime(): number {
    return this.MIN_COLUMNS * this.SPIN_DELAY + this.SPIN_ANIMATION_TIME - 300
  }

  get backgroundColor(): string {
    return this.colorResolver.resolve(this.raffleDrawWidget?.primary_color) || 'transparent'
  }

  get digitColor(): string {
    return this.colorResolver.resolve(this.raffleDrawWidget?.primary_text_color) || '#000000'
  }

  get digitFontSizeCqw(): number {
    const px = this.raffleDrawWidget?.font_size ?? 48
    return (px * 10) / 8
  }

  private get isBulkDrawEnabled(): boolean {
    return this.raffleDrawWidget?.enable_bulk_raffle_draw === true
  }

  private get isConfettiEnabled(): boolean {
    return this.raffleDrawWidget?.enable_confetti === true
  }

  constructor(private colorResolver: GbPresenterColorResolverService) {
    this.destroyRef.onDestroy(() => {
      this.clearAllSpinTimers()
      this.clearQueueTimeout()
      this.drawQueue = []
    })
  }

  ngOnInit() {
    this.raffleDrawWidget$.next(this.raffleDrawWidget)
    this.subscribeToRaffleItemPrizesAvailable()
    this.ensureDrawRows()
    this.initAllRows(0)

    if (this.showTestData) {
      this.setTestData()
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['raffleDrawWidget']) return

    const prev = changes['raffleDrawWidget'].previousValue as RaffleDrawWidget | undefined
    const curr = changes['raffleDrawWidget'].currentValue as RaffleDrawWidget | undefined

    if (!prev || !curr) return

    this.raffleDrawWidget$.next(curr)

    const raffleItemChanged = prev.raffle_item !== curr.raffle_item
    const bulkChanged = prev.enable_bulk_raffle_draw !== curr.enable_bulk_raffle_draw

    if (raffleItemChanged) {
      this.raffleDrawWidget$.next(curr)
      this.subscribeToRaffleItemPrizesAvailable()
    }

    if (bulkChanged) {
      this.ensureDrawRows()
      this.initAllRows(this.lastTicketNumber)
    }
  }

  setTestData() {
    this.ensureDrawRows()
    this.clearAllSpinTimers()
    this.clearQueueTimeout()
    this.drawQueue = []
    this.isQueueProcessing = false

    const ticketNumber = this.getRandomPreviewTicketNumber()
    this.lastTicketNumber = ticketNumber

    this.drawRows.forEach((row, index) => {
      this.setRowToTicketNumberInstant(row, this.getTicketNumberForPrize(ticketNumber, index))
    })

    this.cdr.markForCheck()
  }

  resetPreview(): void {
    this.lastTicketNumber = 0
    this.ensureDrawRows()
    this.clearAllSpinTimers()
    this.initAllRows(0)
  }

  private subscribeToRaffleItemPrizesAvailable(): void {
    this.raffleItemSubscription?.unsubscribe()

    this.raffleItemSubscription = combineLatest([
      this.rafflesFacade.allRaffleItems$,
      this.rafflesFacade.loaded$.pipe(startWith(false)),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(([raffleItems, loaded]) => {
          if (!loaded) return 1
          const raffleItemUuid = this.raffleDrawWidget?.raffle_item
          if (!raffleItemUuid) return 1
          const item = raffleItems.find((i) => i.raffle_item?.uuid === raffleItemUuid)
          return item?.raffle_item?.prizes_available || 1
        }),
      )
      .subscribe((prizesAvailable) => {
        this.prizesAvailable = Math.max(1, prizesAvailable)
        this.ensureDrawRows()
        this.initAllRows(this.lastTicketNumber)
        this.cdr.markForCheck()
      })
  }

  private ensureDrawRows(): void {
    const rowsNeeded = this.isBulkDrawEnabled ? this.prizesAvailable : 1

    if (this.drawRows.length === rowsNeeded) return

    this.clearAllSpinTimers()

    this.drawRows = Array.from({ length: rowsNeeded }, () => ({
      digitColumns: [],
      isAnimating: false,
      spinningIndex: 0,
      spinIntervalId: null,
      spinTimeoutId: null,
    }))
  }

  private initAllRows(baseTicketNumber: number): void {
    this.ensureDrawRows()
    this.drawRows.forEach((row, index) =>
      this.initDigitsForRow(row, this.getTicketNumberForPrize(baseTicketNumber, index)),
    )
  }

  private initDigitsForRow(row: DrawRowState, ticketNumber: number) {
    row.digitColumns = []
    const numAsString = String(ticketNumber).padStart(this.MIN_COLUMNS, '0')

    for (const char of numAsString) {
      const digits: string[] = []
      const numberIndex = this.MIN_FULL_SPINS * 10 + Number(char) + 1

      for (let i = 0; i < numberIndex; i++) {
        digits.push(String(i % 10))
      }

      const column: DigitColumn = {
        digits,
        isSpun: false,
        style: {},
      }

      this.updateColumnStyle(column, row.isAnimating)
      row.digitColumns.push(column)
    }

    this.cdr.markForCheck()
  }

  private updateColumnStyle(column: DigitColumn, isAnimating: boolean): void {
    const columnsShiftAmount = column.isSpun ? column.digits.length - 1 : 0
    const transform = `translateX(-50%) translateY(calc(-1 * ${columnsShiftAmount} * 100cqh)) translateZ(0)`

    if (!isAnimating) {
      column.style = {
        transition: 'none',
        transform,
      }
    } else {
      column.style = {
        transition: `transform ${this.SPIN_ANIMATION_TIME}ms cubic-bezier(0.16, 1, 0.3, 1), filter 280ms ease-out, opacity 280ms ease-out`,
        transform,
      }
    }
  }

  getColumnStyles(digitColumn: DigitColumn, isAnimating: boolean): Record<string, string> {
    return digitColumn.style
  }

  previewDraw(): void {
    this.ensureDrawRows()

    if (this.isBulkDrawEnabled) {
      this.clearAllSpinTimers()
      this.drawRows.forEach((row) => {
        this.prepareRowForAnimation(row, this.getRandomPreviewTicketNumber())
      })
    } else {
      this.spinDraw(this.getRandomPreviewTicketNumber())
      return
    }

    this.executeAnimationBatch()
  }

  spinDraw(ticketNumber: number) {
    this.lastTicketNumber = ticketNumber
    this.ensureDrawRows()
    this.clearAllSpinTimers()

    this.drawRows.forEach((row, index) => {
      this.prepareRowForAnimation(row, this.getTicketNumberForPrize(ticketNumber, index))
    })

    this.executeAnimationBatch()
  }

  triggerDrawFromRealtime(ticketNumber: number): void {
    this.spinDraw(ticketNumber)
  }

  private prepareRowForAnimation(row: DrawRowState, ticketNumber: number): void {
    row.isAnimating = true
    row.spinningIndex = 0
    this.initDigitsForRow(row, ticketNumber)
    row.digitColumns.forEach((col) => this.updateColumnStyle(col, true))
  }

  private executeAnimationBatch(): void {
    this.scheduleConfetti()

    for (const row of this.drawRows) {
      this.animateRow(row)
    }
    this.cdr.markForCheck()
  }

  private animateRow(row: DrawRowState): void {
    if (row.digitColumns.length === 0) {
      row.isAnimating = false
      return
    }

    if (this.SPIN_DELAY <= 0) {
      row.digitColumns.forEach((col) => this.spinDigitColumn(col, row.isAnimating))
      this.finishSpinAfterAnimation(row)
      return
    }

    this.spinDigitColumn(row.digitColumns[row.spinningIndex], row.isAnimating)

    row.spinIntervalId = setInterval(() => {
      row.spinningIndex++

      if (row.spinningIndex >= row.digitColumns.length) {
        this.clearSpinInterval(row)
        this.finishSpinAfterAnimation(row)
        return
      }

      this.spinDigitColumn(row.digitColumns[row.spinningIndex], row.isAnimating)
      this.cdr.markForCheck()
    }, this.SPIN_DELAY)
  }

  private spinDigitColumn(column: DigitColumn, isAnimating: boolean) {
    column.isSpun = true
    this.updateColumnStyle(column, isAnimating)
  }

  private finishSpinAfterAnimation(row: DrawRowState): void {
    row.spinTimeoutId = setTimeout(
      () => {
        row.isAnimating = false
        row.digitColumns.forEach((col) => this.updateColumnStyle(col, false))
        this.cdr.markForCheck()
      },
      Math.max(0, this.SPIN_ANIMATION_TIME - this.LANDING_LEAD_TIME_MS),
    )
  }

  private getRandomPreviewTicketNumber(): number {
    return Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000
  }

  private setRowToTicketNumberInstant(row: DrawRowState, ticketNumber: number): void {
    row.isAnimating = false
    row.spinningIndex = 0
    this.initDigitsForRow(row, ticketNumber)

    for (const col of row.digitColumns) {
      col.isSpun = true
      this.updateColumnStyle(col, false)
    }
  }

  private getTicketNumberForPrize(baseTicketNumber: number, prizeIndex: number): number {
    if (!this.isBulkDrawEnabled) {
      return baseTicketNumber
    }
    const digit = (9 - (prizeIndex % 10) + 10) % 10
    return Number(String(digit).repeat(this.MIN_COLUMNS))
  }

  private scheduleConfetti(): void {
    if (this.isConfettiEnabled) {
      setTimeout(() => {
        this.fireConfetti()
      }, this.totalAnimationTime)
    }
  }

  private fireConfetti(): void {
    this.confettiRequested.emit()
  }

  private clearQueueTimeout(): void {
    if (this.queueTimeoutId) {
      clearTimeout(this.queueTimeoutId)
      this.queueTimeoutId = null
    }
  }

  private clearSpinInterval(row: DrawRowState): void {
    if (row.spinIntervalId) {
      clearInterval(row.spinIntervalId)
      row.spinIntervalId = null
    }
  }

  private clearSpinTimeout(row: DrawRowState): void {
    if (row.spinTimeoutId) {
      clearTimeout(row.spinTimeoutId)
      row.spinTimeoutId = null
    }
  }

  private clearAllSpinTimers(): void {
    for (const row of this.drawRows) {
      this.clearSpinInterval(row)
      this.clearSpinTimeout(row)
    }
  }
}
